import { Box, Chip, Tooltip } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { CLOSED, DRAFT, IN_PROGRESS, NOT_OPENED, PUBLISHED, SENT, SUBMITTED } from '../../constants';
import { camelCase } from '../../helpers/general';
import { IdName, SortDirection, TableRowsProps } from '../../models/global';
import {
  CountryInterface,
  EmailInterface,
  OrganizationInterface,
  OrganizationSurveyInterface,
  SeriesListInterface,
  UserInteface,
} from '../../models/state';

interface TableProps {
  columns: string[];
  rows:
    | TableRowsProps[]
    | OrganizationInterface[]
    | OrganizationSurveyInterface[]
    | SeriesListInterface[]
    | UserInteface[]
    | EmailInterface[]
    | CountryInterface[];
  page: number;
  count?: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setPage?: (page: number) => void;
  rowClickHandler: (id: string) => void;
  orderBy?: string;
  handleOrder?: (val: string) => void;
  sortDirection?: SortDirection;
  handleSortDirection?: (val: SortDirection) => void;
  actionIcons?: {
    title: string;
    icon: ReactElement;
    onClick: (id: string) => void;
  }[];
  rowSelected?: string;
  rowsUnsortable?: string[];
}

function TableComponent({
  columns,
  rows,
  count,
  page,
  rowsPerPage,
  rowsPerPageOptions,
  onRowsPerPageChange,
  setPage,
  rowClickHandler,
  orderBy,
  handleOrder = () => {},
  sortDirection,
  handleSortDirection = () => {},
  actionIcons,
  rowSelected,
  rowsUnsortable,
}: TableProps) {
  const getColumnValue = (key: string, row: TableRowsProps) => {
    const columnAsKey = camelCase(key);
    if (typeof row[columnAsKey] === 'boolean') {
      return row[columnAsKey] ? 'Yes' : 'No';
    }
    if (!row[columnAsKey]) {
      return 'N/A';
    }
    if (key === 'Status' || key === 'Progress') {
      const rowValue = row[columnAsKey] as string;
      return (
        <Chip
          label={rowValue}
          color={
            [DRAFT, IN_PROGRESS].includes(rowValue) || rowValue.includes(IN_PROGRESS)
              ? 'primary'
              : [PUBLISHED, SUBMITTED, SENT].includes(rowValue)
                ? 'success'
                : [CLOSED, NOT_OPENED].includes(rowValue)
                  ? 'error'
                  : 'info'
          }
          sx={{ width: 145, fontSize: 15 }}
        />
      );
    }
    if (key === 'Organization') {
      return (
        <Link
          style={{ textDecoration: 'none' }}
          onClick={e => e.stopPropagation()}
          to={`/organizations/${(row[columnAsKey] as IdName)?.id}`}
        >
          {(row[columnAsKey] as IdName).name}
        </Link>
      );
    }
    return String(row[columnAsKey]);
  };

  const handleSorting = (column: string) => {
    if (column === orderBy) {
      handleSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      handleOrder(column);
      handleSortDirection('desc');
    }
    setPage(0);
  };

  if (!rows.length) {
    return <Box textAlign='center'>No Data</Box>;
  }
  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell sx={{ fontWeight: 'bold' }} key={column}>
                {orderBy && !rowsUnsortable?.includes(column) ? (
                  <TableSortLabel
                    active={orderBy === column}
                    direction={sortDirection}
                    onClick={() => handleSorting(column)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {column}
                  </TableSortLabel>
                ) : (
                  column
                )}
              </TableCell>
            ))}
            {!!actionIcons?.length && <TableCell></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              onClick={() => rowClickHandler(row.id as string)}
              key={row.id as string}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                backgroundColor: !!row.id && rowSelected === row.id ? '#f5f5f5' : 'inherit',
              }}
              hover
            >
              {columns.map((el: string) => (
                <TableCell key={el} component='th' scope='row'>
                  {getColumnValue(el, row)}
                </TableCell>
              ))}
              {!!actionIcons?.length && (
                <TableCell key='icon' component='th' scope='row'>
                  {actionIcons.map((el, i) => {
                    const icon =
                      rowSelected === row.id
                        ? React.cloneElement(el.icon, {
                            checked: true,
                            onChange: () => {},
                          })
                        : el.icon;
                    return (
                      <Tooltip key={i} title={el.title}>
                        <span
                          onClick={e => {
                            e.stopPropagation();
                            el.onClick(row.id as string);
                          }}
                        >
                          {icon}
                        </span>
                      </Tooltip>
                    );
                  })}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component='div'
        count={count}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions || []}
        onRowsPerPageChange={onRowsPerPageChange}
        hidden={!setPage}
      />
    </TableContainer>
  );
}

export default TableComponent;
